import React, { useState, useEffect } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowLeftRight } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CURRENCY_FORMATS = {
  AUD: { locale: 'en-AU' },
  USD: { locale: 'en-US' },
  IDR: { locale: 'id-ID' },
  PHP: { locale: 'en-PH' },
  THB: { locale: 'th-TH' },
  VND: { locale: 'vi-VN' },
  MYR: { locale: 'ms-MY' },
};

function formatCurrency(amount, currency) {
  const num = Number(amount) || 0;
  const cur = currency || 'AUD';
  const fmt = CURRENCY_FORMATS[cur];
  if (fmt) {
    try {
      return new Intl.NumberFormat(fmt.locale, {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: cur === 'IDR' || cur === 'VND' ? 0 : 2,
      }).format(num);
    } catch { /* fall through */ }
  }
  return `${cur} ${num.toLocaleString()}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

const getStatusBadgeClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'settled') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (s === 'pending' || s === 'processing') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (s === 'failed' || s === 'rejected') return 'border-red-200 bg-red-50 text-red-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const getTypeBadgeClass = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'payout' || t === 'withdrawal') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (t === 'payin' || t === 'deposit') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const BackOfficeFinanceTransactions = () => {
  const { isLive } = useEnvironment();
  const env = isLive ? 'live' : 'sandbox';
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/analytics/summary`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'X-Environment': env,
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setTransactions(data?.recent_transactions || data?.transactions || []);
      } catch (err) {
        toast.error('Failed to load transactions');
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, [env]);

  if (loading) {
    return (
      <BackOfficeLayout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <div className="spinner w-8 h-8" />
          <p className="text-sm text-slate-500">Loading transactions…</p>
        </div>
      </BackOfficeLayout>
    );
  }

  return (
    <BackOfficeLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <ArrowLeftRight className="w-7 h-7 text-[#0B6445]" />
            All Transactions
          </h1>
          <p className="text-[#0E1B16]/70 mt-2 mb-6">
            Read-only view of recent transactions across the platform.
          </p>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-base text-[#073B2E]">Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No transactions found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#DCE4E0]">
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Date</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Counterparty</th>
                      <th className="text-right py-3 px-2 font-semibold text-[#073B2E]">Amount</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Currency</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Type</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => (
                      <tr key={tx.id || tx._id || i} className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC]">
                        <td className="py-3 px-2 text-[#0E1B16]/70">
                          {formatDate(tx.date || tx.created_at || tx.timestamp)}
                        </td>
                        <td className="py-3 px-2 font-medium text-[#073B2E]">
                          {tx.counterparty || tx.counterparty_name || tx.recipient || tx.sender || '—'}
                        </td>
                        <td className="py-3 px-2 text-right font-medium text-[#073B2E]">
                          {formatCurrency(tx.amount, tx.currency)}
                        </td>
                        <td className="py-3 px-2">{tx.currency || '—'}</td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={getTypeBadgeClass(tx.type)}>
                            {tx.type || '—'}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={getStatusBadgeClass(tx.status)}>
                            {tx.status || 'unknown'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeFinanceTransactions;
